/* eslint-disable jsx-a11y/anchor-is-valid */
import { map, noop, mapObject, constant } from 'underscore';
import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { conversionTagProp, withConversionTagData, ConversionDataContext, useInitConversionTagData, isDataReady } from '@bingads-webui-campaign-react/conversion-data';
import { SidePanel as Panel } from '@bingads-webui-campaign-react/side-panel';
import { TextField, DefaultButton, Link, PanelType } from '@fluentui/react';

import { EmailTemplate } from './email-template';
import { EventTagEmailPreview } from './event-tag-email-preview';
import { MAX_EMAIL_ADDRESSES_COUNT, splitEmailAddresses, genEmailAddresses, canSendEmail } from '../utils/email-tag';
import { getEmailTagViewStyle, emailTagPanelStyles } from '../styles/email-tag-view-styles';
import { generateEmailContent } from '../utils/generate-email-content';

const EmailTagViewUnstyled = (props) => {
  const {
    tagContext,
    onSendEmail,
    classes,
    action,
    currencyCode,
    goalValue,
    goalValueType,
    isEventGoal,
  } = props;
  const {
    data,
    emailStatus,
    emailInProgress,
    sendTagEmail,
  } = tagContext;
  const i18n = useContext(I18nContext);
  const { appContext: { isReadOnlyUser }, instrumentation } = useContext(ConversionDataContext);

  const [isFirstRendered, setIsFirstRendered] = useState(true);
  const [emailAddresses, setEmailAddresses] = useState(null);
  const [errorText, setErrorText] = useState(null);
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);

  const genErrorMessage = useCallback((validEmailAddresses, invalidEmailAddresses) => {
    if (isFirstRendered) {
      setIsFirstRendered(false);
      return (null);
    }
    const exceedMaxValidMessage = i18n.getString(_TL_('You have reached the maximum of {0} email addresses. Remove at least 1 email address to send the message.')).split('{0}');

    return (
      <div className="show-error">
        {validEmailAddresses.length > MAX_EMAIL_ADDRESSES_COUNT &&
          <div className="exceed-max-valid-address">
            {`${exceedMaxValidMessage[0]}${MAX_EMAIL_ADDRESSES_COUNT}${exceedMaxValidMessage[1]}`}
          </div>
        }
        {validEmailAddresses.length === 0 &&
          <div className="no-valid-email-address">
            {i18n.getString(_TL_('Expect at least one valid email address.'))}
          </div>
        }
        {invalidEmailAddresses.length > 0 &&
          <div className="invalid-email-address">
            <div className="title">
              {i18n.getString(_TL_('Please correct these invalid email addresses:'))}
            </div>
            {map(invalidEmailAddresses, (address, idx) => (
              <li key={idx} className={`invalid-${idx}`}>
                {address}
              </li>
            ))}
          </div>
        }
      </div>
    );
  }, [isFirstRendered, i18n]);

  const onChangeEmailAddress = useCallback((textString) => {
    instrumentation.logTrace(`[EmailTagView] Email address change to ${textString}`);
    const userInputs = textString.split('\n');
    const [validEmailAddresses, invalidEmailAddresses] = splitEmailAddresses(userInputs);
    setErrorText(genErrorMessage(validEmailAddresses, invalidEmailAddresses));
    setEmailAddresses(userInputs);
  }, [instrumentation, genErrorMessage]);

  // generate the key/value pairs needed by the email template
  const emailContent = useMemo(() => generateEmailContent({
    i18n,
    tagId: data && data.id,
    action,
    currencyCode,
    goalValueType,
  }), [
    i18n,
    data,
    action,
    currencyCode,
    goalValueType,
  ]);

  const sendEmail = useCallback(() => {
    const [validEmailAddresses, invalidEmailAddresses] = splitEmailAddresses(emailAddresses);
    if (canSendEmail(validEmailAddresses, invalidEmailAddresses)) {
      const targetEmailAddresses = genEmailAddresses(validEmailAddresses);
      instrumentation.logTrace(`[EmailTagView] Send email to ${targetEmailAddresses}, emailContent=${JSON.stringify(emailContent)}`);
      if (!emailInProgress) {
        sendTagEmail(emailContent, targetEmailAddresses);
        onSendEmail();
      }
    } else {
      if (validEmailAddresses.length > MAX_EMAIL_ADDRESSES_COUNT) {
        instrumentation.logTrace(`[EmailTagView] Find ${validEmailAddresses.length} valid email addresses. Reached the maximum of ${MAX_EMAIL_ADDRESSES_COUNT} email addresses.`);
      }
      if (validEmailAddresses.length === 0) {
        instrumentation.logTrace('[EmailTagView] Expect at least one valid email addresses.');
      }
      if (invalidEmailAddresses.length > 0) {
        instrumentation.logTrace(`[EmailTagView] Find invalid email address(es): ${invalidEmailAddresses.toString()}`);
      }
    }
  }, [emailContent, emailInProgress, sendTagEmail, emailAddresses, instrumentation, onSendEmail]);

  useEffect(() => {
    if (emailStatus === true) {
      notificationApi.showInfo(i18n.getString(_TL_('Email sent')));
    } else if (emailStatus === false) {
      notificationApi.showWarning(i18n.getString(_TL_('Oh, no! We weren\'t able to send the email message. Please try again.')));
    }
  }, [emailStatus, i18n]);

  const onShowEmailTemplate = useCallback(() => {
    instrumentation.logTrace('[EmailTagView] Show email template');
    setShowEmailTemplate(true);
  }, [instrumentation]);

  const onCloseEmailTemplate = useCallback(() => {
    instrumentation.logTrace('[EmailTagView] Close email template');
    setShowEmailTemplate(false);
  }, [instrumentation]);

  const onRenderPanelFooter = useCallback(() => (
    <DefaultButton onClick={onCloseEmailTemplate}>
      {i18n.getString(_TL_('Close'))}
    </DefaultButton>
  ), [i18n, onCloseEmailTemplate]);

  useInitConversionTagData(tagContext);

  return (
    <div className={classNames(classes.container, 'email-tag-view')}>
      <div className="description">
        {i18n.getString(_TL_('Enter the email address for the person who will add the tracking tag to your website\'s code. For more than one email address, enter one address per line. We\'ll send instructions and the tracking tag to them, and you.'))}
      </div>
      <TextField
        multiline
        rows={10}
        placeholder={i18n.getString(_TL_('Enter an email address. For multiple addresses, enter one per line.'))}
        className="email-text-area"
        validateOnFocusOut
        onGetErrorMessage={onChangeEmailAddress}
      />
      {errorText}
      <DefaultButton
        onClick={sendEmail}
        text={i18n.getString(_TL_('Send'))}
        className="send-email"
        disabled={isReadOnlyUser}
      />

      <Link
        onClick={onShowEmailTemplate}
        className="email-template-link"
      >
        {i18n.getString(_TL_('View email template'))}
      </Link>

      {isDataReady(data) &&
        <Panel
          isOpen={showEmailTemplate}
          onOuterClick={noop}
          onDismiss={onCloseEmailTemplate}
          type={PanelType.custom}
          customWidth={emailTagPanelStyles.panel.width[data.type]}
          headerText={i18n.getString(_TL_('Email preview'))}
          onRenderFooterContent={onRenderPanelFooter}
          isFooterAtBottom
          className={classNames(classes.panel, 'email-template-panel')}
          closeButtonAriaLabel={i18n.getString(_TL_('Close email template'))}
          isLightDismiss
          styles={emailTagPanelStyles.panel}
          focusTrapZoneProps={{
            forceFocusInsideTrap: false,
          }}
        >
          {!isEventGoal && (
            <EmailTemplate tag={data} />
          )}

          {isEventGoal && (
            <EventTagEmailPreview
              action={action}
              currencyCode={currencyCode}
              goalValue={goalValue}
              goalValueType={goalValueType}
            />
          )}
        </Panel>
      }
    </div>
  );
};

EmailTagViewUnstyled.propTypes = {
  tagContext: conversionTagProp.isRequired,
  onSendEmail: PropTypes.func,
  classes: PropTypes.shape(mapObject(
    getEmailTagViewStyle(),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  action: PropTypes.string,
  currencyCode: PropTypes.string,
  goalValue: PropTypes.string,
  goalValueType: PropTypes.oneOf(['FixedValue', 'VariantValue', 'NoValue']),
  isEventGoal: PropTypes.bool,
};

EmailTagViewUnstyled.defaultProps = {
  action: undefined,
  currencyCode: undefined,
  goalValue: undefined,
  goalValueType: undefined,
  isEventGoal: false,
  onSendEmail: noop,
};

export const EmailTagView = withDefaultStyles(
  withConversionTagData(EmailTagViewUnstyled),
  getEmailTagViewStyle
);
